/**
 * TopNav.tsx — Top navigation bar. Ported from prototype shell.jsx `TopNav`.
 *
 * The user menu + identity are wired to the REAL Auth0 session (name, email,
 * role claim, real logout). The organization chip shows the real org from the
 * token and nothing else — no fake alternatives. Global search has no backend
 * yet and is visibly disabled as "In arrivo"; the notifications dropdown is
 * honestly empty (real completion alerts arrive as toasts via JobsWatcher).
 *
 * PDF export was REMOVED here on 2026-07-29 (Omar's decision): the feature is
 * dropped from scope rather than deferred, so an "In arrivo" affordance would
 * be a promise nothing intends to keep. See the wiki finding
 * 2026-07-29-pdf-export-dropped.
 */
import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useIsSuperAdmin } from "../../lib/kbApi";
import { I, type IconComponent } from "../icons";
import { DropdownMenu } from "../ui/DropdownMenu";
import { Logo } from "./Logo";

const ROLE_CLAIM = "https://cme-studio.app/role";
const ORG_NAME_CLAIM = "https://cme-studio.app/org_name";

interface TopNavProps {
  breadcrumb: string[];
  sidebarVisible: boolean;
  onToggleSidebar: () => void;
}

function initials(name?: string, email?: string): string {
  const src = name?.trim() || email?.split("@")[0] || "?";
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function MenuItem({
  icon: Ico,
  label,
  danger,
  onClick,
}: {
  icon: IconComponent;
  label: string;
  danger?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        border: "none",
        cursor: "pointer",
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        textAlign: "left",
        padding: "8px 10px",
        borderRadius: 7,
        background: "transparent",
        fontFamily: "inherit",
        fontSize: 12.5,
        fontWeight: 500,
        color: danger ? "var(--c-err)" : "var(--c-ink)",
        transition: "background var(--tr-fast)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = danger ? "var(--c-err-soft)" : "rgba(20,22,28,0.04)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <Ico size={15} style={{ color: danger ? "var(--c-err)" : "var(--c-ink-3)", flexShrink: 0 }} />
      <span>{label}</span>
    </button>
  );
}

export function TopNav({ breadcrumb, onToggleSidebar }: TopNavProps): JSX.Element {
  const { user, logout } = useAuth0();
  const isSuperAdmin = useIsSuperAdmin();
  const navigate = useNavigate();
  const [userOpen, setUserOpen] = useState(false);

  const claims = (user ?? {}) as Record<string, unknown>;
  const role = typeof claims[ROLE_CLAIM] === "string" ? (claims[ROLE_CLAIM] as string) : "Membro";
  // Real organization name from the token (namespaced claim, or Auth0's plain
  // org_name). When neither is present the chip is simply hidden — never a
  // fabricated company name.
  const orgName =
    typeof claims[ORG_NAME_CLAIM] === "string"
      ? (claims[ORG_NAME_CLAIM] as string)
      : typeof claims["org_name"] === "string"
        ? (claims["org_name"] as string)
        : null;

  const name = user?.name ?? user?.email ?? "Utente";
  const email = user?.email ?? "";
  const mono = initials(user?.name, user?.email);

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        height: 52,
        borderBottom: "1px solid var(--c-line)",
        background: "var(--c-bg-elev)",
        paddingLeft: 16,
        paddingRight: 12,
        gap: 12,
        flexShrink: 0,
        zIndex: 10,
      }}
    >
      <button
        className="btn btn-ghost btn-icon btn-sm"
        onClick={onToggleSidebar}
        title="Mostra/nascondi sidebar"
      >
        <I.Sidebar size={15} />
      </button>
      <div style={{ width: 1, height: 18, background: "var(--c-line)" }} />
      <Logo />

      {/* Organization (Auth0 Organizations) — display-only, real org */}
      {orgName && (
        <>
          <div style={{ width: 1, height: 18, background: "var(--c-line)", marginLeft: 4 }} />
          <div
            title="Organizzazione"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              height: 30,
              paddingLeft: 7,
              paddingRight: 8,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: 5,
                flexShrink: 0,
                background: "var(--c-brand-soft)",
                color: "var(--c-brand-ink)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <I.Building size={11} />
            </span>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                color: "var(--c-ink)",
                whiteSpace: "nowrap",
              }}
            >
              {orgName}
            </span>
          </div>
        </>
      )}
      <div style={{ width: 1, height: 18, background: "var(--c-line)" }} />

      {/* Breadcrumb */}
      <nav
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 13,
          color: "var(--c-ink-2)",
          minWidth: 0,
          flex: 1,
        }}
      >
        {breadcrumb.map((b, i) => (
          <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {i > 0 && <I.Chevron size={12} style={{ color: "var(--c-ink-3)" }} />}
            <span
              style={{
                color: i === breadcrumb.length - 1 ? "var(--c-ink)" : "var(--c-ink-3)",
                fontWeight: i === breadcrumb.length - 1 ? 500 : 400,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 280,
              }}
            >
              {b}
            </span>
          </span>
        ))}
      </nav>

      {/* Search — In arrivo (no backend yet) */}
      <div style={{ position: "relative" }} title="Ricerca globale — in arrivo">
        <I.Search
          size={14}
          style={{
            position: "absolute",
            left: 9,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--c-ink-3)",
          }}
        />
        <input
          className="input"
          placeholder="Ricerca — in arrivo"
          disabled
          style={{ width: 220, paddingLeft: 30, height: 30, fontSize: 12.5, opacity: 0.6 }}
        />
      </div>

      {/* Notifications — honestly empty; completion alerts arrive as
          JobsWatcher toasts. Becomes a real feed when a notifications
          backend exists. */}
      <DropdownMenu
        align="right"
        width={280}
        header="Notifiche"
        items={[]}
        emptyLabel="Nessuna nuova notifica. Al termine di un'analisi riceverai un avviso in basso a destra."
        renderTrigger={(toggle) => (
          <button className="btn btn-ghost btn-icon btn-sm" title="Notifiche" onClick={toggle}>
            <I.Bell size={15} />
          </button>
        )}
      />

      {/* User menu — real Auth0 identity */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <button
          onClick={() => setUserOpen((o) => !o)}
          title="Account"
          style={{
            appearance: "none",
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            display: "block",
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #284B78, #1E3A5F)",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.02em",
              boxShadow: userOpen ? "0 0 0 2px var(--c-bg-elev), 0 0 0 4px var(--c-brand)" : "none",
              transition: "box-shadow var(--tr-fast)",
            }}
          >
            {mono}
          </div>
        </button>
        {userOpen && (
          <>
            <div
              onClick={() => setUserOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 40 }}
            />
            <div
              className="fade-in"
              style={{
                position: "absolute",
                top: "calc(100% + 8px)",
                right: 0,
                width: 252,
                zIndex: 41,
                background: "var(--c-bg-elev)",
                border: "1px solid var(--c-line)",
                borderRadius: 10,
                boxShadow: "var(--sh-pop)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  padding: "12px 14px",
                  borderBottom: "1px solid var(--c-line)",
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    flexShrink: 0,
                    background: "linear-gradient(135deg, #284B78, #1E3A5F)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {mono}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-ink)" }}>{name}</div>
                  {email && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--c-ink-3)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {email}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "var(--c-ink-3)", marginTop: 2 }}>
                    {orgName ? `${role} · ${orgName}` : role}
                  </div>
                </div>
              </div>
              <div style={{ padding: 6 }}>
                {/* Platform administration. In the account menu rather than the
                    sidebar because it is not a stage of a tender — and shown only
                    to the vendor, since it is the one screen that lists every
                    customer. */}
                {isSuperAdmin && (
                  <MenuItem
                    icon={I.Settings}
                    label="Amministrazione piattaforma"
                    onClick={() => navigate("/admin")}
                  />
                )}
                <MenuItem
                  icon={I.External}
                  label="Esci"
                  danger
                  onClick={() => {
                    logout({ logoutParams: { returnTo: window.location.origin } });
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
